import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import { Heading } from "../components/Heading";
import Paginator from "../components/Paginator";
import ConfirmationModal from "../components/ConfirmationModal";
import { Loading } from "../components/Loading";
import PaymentsFilters from "../components/payments/PaymentsFilters";
import PaymentsTable from "../components/payments/PaymentsTable";
import BillingDataModal from "../components/payments/BillingDataModal";
import { PaymentsService } from "../services/PaymentsService";
import { urlApp } from "../services/Endpoints";
import { downloadDataUrl, isPublic } from "../components/payments/utils";

const ITEMS_PER_PAGE = 50;

const appBase = urlApp ? urlApp.replace(/\/dashboard\/?$/, '') : '';

export function Payments() {
    const navigate = useNavigate();
    const [meta, setMeta] = useState({ accounts: [], max_date: '' });
    const [filters, setFilters] = useState({ account: '-1', mode: 'monthly', dateStart: '', dateEnd: '' });
    const [items, setItems] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [expanded, setExpanded] = useState({});
    const [billingModal, setBillingModal] = useState({ open: false, account: '' });
    const [confirmPublish, setConfirmPublish] = useState(false);
    const [notice, setNotice] = useState(null);
    const [searched, setSearched] = useState(false);

    const period = useMemo(() => {
        if (filters.mode === 'annual') return filters.dateStart ? filters.dateStart.slice(0, 4) : '';
        return filters.dateStart;
    }, [filters.mode, filters.dateStart]);

    const dateStartForApi = useMemo(() => {
        if (filters.mode === 'annual') return filters.dateStart ? filters.dateStart.slice(0, 4) : '';
        return filters.dateStart;
    }, [filters.mode, filters.dateStart]);

    const showNotice = (type, text) => {
        setNotice({ type, text });
        setTimeout(() => setNotice(null), 6000);
    };

    const loadMeta = useCallback(async () => {
        try {
            const data = await PaymentsService.getMeta();
            setMeta(data);
            const start = data.max_date ? data.max_date.slice(0, 7) : '';
            setFilters((prev) => ({ ...prev, dateStart: prev.dateStart || start, dateEnd: prev.dateEnd || start }));
        } catch (err) {
            showNotice('error', err.message);
        }
    }, []);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    const showPayments = useCallback(async () => {
        if (!period) {
            showNotice('error', 'Selecciona un periodo válido');
            return;
        }
        setLoading(true);
        setSearched(true);
        try {
            const res = await PaymentsService.getPayments({
                account: filters.account,
                date: period,
                filter_mode: filters.mode,
                date_start: dateStartForApi,
                date_end: filters.dateEnd,
            });
            setItems(res.data);
            setResumen(res.resumen);
            setCurrentPage(1);
            setExpanded({});
        } catch (err) {
            showNotice('error', err.message);
        } finally {
            setLoading(false);
        }
    }, [period, dateStartForApi, filters.account, filters.mode, filters.dateEnd]);

    const fetchChannels = useCallback(async (row) => {
        setExpanded((prev) => ({ ...prev, [row.publisher]: 'loading' }));
        try {
            const channels = await PaymentsService.getUsdByChannel({
                account: row.publisher,
                account_id: row.id_pub,
                date: period,
                filter_mode: filters.mode,
                date_start: dateStartForApi,
                date_end: filters.dateEnd,
            });
            setExpanded((prev) => ({ ...prev, [row.publisher]: channels }));
        } catch (err) {
            showNotice('error', err.message);
            setExpanded((prev) => {
                const next = { ...prev };
                delete next[row.publisher];
                return next;
            });
        }
    }, [period, dateStartForApi, filters.mode, filters.dateEnd]);

    const toggleChannels = (row) => {
        if (expanded[row.publisher] !== undefined) {
            setExpanded((prev) => {
                const next = { ...prev };
                delete next[row.publisher];
                return next;
            });
            return;
        }
        fetchChannels(row);
    };

    const notifyReportPublished = async (accountsToNotify) => {
        if (!accountsToNotify.length) return;
        try {
            const res = await PaymentsService.sendReportPublished(accountsToNotify, period);
            const ok = res.status === 'success';
            showNotice(ok ? 'success' : 'error', res.msg || (ok ? 'Reporte publicado' : 'No se pudo enviar la notificación'));
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handlePublishPeriod = async () => {
        setConfirmPublish(false);
        if (!period) {
            showNotice('error', 'Selecciona un periodo válido');
            return;
        }
        setPublishing(true);
        try {
            const notPublic = items
                .filter((r) => r.total > 0 && !isPublic(r.public))
                .map((r) => r.publisher);
            await PaymentsService.postPayments({ account: filters.account, date: period });
            showNotice('success', 'Periodo publicado correctamente');
            await showPayments();
            await notifyReportPublished(notPublic);
        } catch (err) {
            showNotice('error', err.message);
        } finally {
            setPublishing(false);
        }
    };

    const handlePublish = async (row) => {
        try {
            await PaymentsService.postPayments({ account: row.publisher, date: period, ink_id: row.ink_id });
            showNotice('success', `Cuenta ${row.publisher} publicada`);
            await showPayments();
            await notifyReportPublished([row.publisher]);
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handlePublishChannel = async (row, channel) => {
        try {
            await PaymentsService.postPayments({
                account: row.publisher,
                date: period,
                channel_id: channel.id,
                ink_id: row.ink_id,
            });
            showNotice('success', `Canal ${channel.channel} publicado`);
            await fetchChannels(row);
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handleDownloadAll = async () => {
        try {
            const file = await PaymentsService.downloadAll({
                account: filters.account,
                date: period,
                filter_mode: filters.mode,
                date_start: dateStartForApi,
                date_end: filters.dateEnd,
                lang: 'es',
            });
            downloadDataUrl(file, `financial_report_${period}.xls`);
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handleDownloadXls = async (row) => {
        try {
            const file = await PaymentsService.downloadAccount({
                account: row.publisher,
                account_id: row.id_pub,
                date: period,
                filter_mode: filters.mode,
                date_start: dateStartForApi,
                date_end: filters.dateEnd,
                lang: 'es',
            });
            downloadDataUrl(file, `financial_report_${period}.xls`);
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handleView = async (row) => {
        try {
            await PaymentsService.changeSession(row.publisher);
            if (appBase) {
                window.open(`${appBase}/reports/sales?period=${period}`, '_blank');
            } else {
                showNotice('error', 'URL de la app no configurada (REACT_APP_APP_INK_URL)');
            }
        } catch (err) {
            showNotice('error', err.message);
        }
    };

    const handleBilling = (row) => {
        setBillingModal({ open: true, account: row.publisher });
    };

    const pagedItems = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return items.slice(start, start + ITEMS_PER_PAGE);
    }, [items, currentPage]);

    return (
        <>
            <Heading title="Ventas" />

            <div className="container mt-5">
                <div className="d-flex justify-content-end mb-3">
                    <button
                        type="button"
                        className="btn btn-ink-search"
                        onClick={() => navigate('/financial')}
                        title="Ir al módulo de Financieros"
                    >
                        <FontAwesomeIcon icon={faCoins} className="me-2" />
                        Financieros
                    </button>
                </div>

                {notice && (
                    <div className={`alert ${notice.type === 'success' ? 'alert-success' : 'alert-danger'} mb-3`}>
                        {notice.text}
                    </div>
                )}

                <PaymentsFilters
                    accounts={meta.accounts}
                    maxDate={meta.max_date}
                    filters={filters}
                    onChange={setFilters}
                    onSearch={showPayments}
                    onPublishPeriod={() => setConfirmPublish(true)}
                    onDownloadAll={handleDownloadAll}
                    loading={loading}
                    publishing={publishing}
                />

                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <PaymentsTable
                            items={pagedItems}
                            resumen={resumen}
                            expanded={expanded}
                            searched={searched}
                            onToggleChannels={toggleChannels}
                            onPublish={handlePublish}
                            onPublishChannel={handlePublishChannel}
                            onDownloadXls={handleDownloadXls}
                            onView={handleView}
                            onBilling={handleBilling}
                        />
                        {items.length > ITEMS_PER_PAGE && (
                            <Paginator
                                totalItems={items.length}
                                itemsPerPage={ITEMS_PER_PAGE}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </div>

            <BillingDataModal
                isOpen={billingModal.open}
                onClose={() => setBillingModal({ open: false, account: '' })}
                account={billingModal.account}
            />

            <ConfirmationModal
                isOpen={confirmPublish}
                onClose={() => setConfirmPublish(false)}
                onConfirm={handlePublishPeriod}
                title="Publicar periodo"
                message={`¿Deseas publicar las ventas del periodo ${period}? Se notificará por correo a las cuentas afectadas.`}
                confirmText="Publicar"
                type="success"
            />
        </>
    );
}

export default Payments;